import { Form, Formik } from "formik"
import { Alert, Button, Col, Row } from "react-bootstrap"
import { PropertyBookingEntry } from "../../../Types/bookingTypes"
import { CheckBoxInputField } from "../../FormFields/CheckBoxInput/CheckBoxInputField"
import { DateInputField } from "../../FormFields/DateInput/DateInputField"
import { DateRangeInputField } from "../../FormFields/DateInput/DateRangeInputField"
import { SelectInputField } from "../../FormFields/SelectInput/SelectInputField"
import { TextInputField } from "../../FormFields/TextInput/TextInputField"
import { bookingFeesFieldValues, bookingSourceFieldValues, bookingSourceFieldValuesRef, bookingStatusFieldValues, cancellationAmountTypeFieldValues } from "./BookingFormConstants"
import { blankBooking, bookingSchema } from "./BookingFormHelpers"
import timeService from "../../../services/time/timeService"
import { getChildProperties } from "../../../Helpers/Property/multiUnit"
import Collapsible from "../../Collapsible/Collapsible"
import canAccess from "../../../services/accessService/accessService"
import { ArrayFieldInput } from "../../FormFields/ArrayInput/ArrayFieldInput"
import { linkedServicesFieldValues } from "../commonFieldValues"
import { useState } from "react"
import SelectUserModal from "../../Modals/SelectUserModal/selectUserModal"
import { useGetResourceAccessListQuery } from "../../../services/bClientApi"
import { PhoneNumberInputField } from "../../FormFields/PhoneNumberInput/PhoneNumberInputField"
import { hasLinkedService } from "../../../Helpers/Property/hasLinkedService"
import BookingLinkedServices from "./BookingLinkedServices"

export interface PropertyBookingFormEntry extends PropertyBookingEntry {
  dateRange: any,
  earnings: any,
}

export default function BookingForm(props: {
  disabled: boolean,
  onSubmit: (v: any) => Promise<any>,
  initialValues?: Partial<PropertyBookingEntry>,
  propertyOptionList: { value: string, text: string }[],
  propertyData: { [pId: string]: any },
  dateValidationFn?: (propertyId: string, checkIn: Date, checkOut: Date) => Promise<boolean>,
  dateValidationStatus?: { available: boolean, message: string, status: string },
  quickBooking?: boolean,
  ownerBooking?: boolean,
}) {
  const {
    disabled,
    onSubmit,
    initialValues,
    propertyOptionList,
    propertyData,
    dateValidationFn,
    dateValidationStatus,
    quickBooking,
    ownerBooking
  } = props

  const [removeCleaningButtonActive, setRemoveCleaningButtonActive] = useState(true)
  const [selectUserModal, setSelectUserModal] = useState(false)
  const [selectOwnerModal, setSelectOwnerModal] = useState(false)
  const [formPropertyId, setFormPropertyId] = useState<string>(initialValues?.propertyId || '')

  const { data: propertyAccessData } = useGetResourceAccessListQuery({
    resourceAccessQuery: {
      resourceType: 'property',
      resourceId: formPropertyId,
      role: ['owner'],
    },
    pagination: {
      page: 1,
      limit: 150,
      all: true,
    }
  }, {
    skip: !formPropertyId,
  })

  const propertyOwnerIds = (propertyAccessData?.docs || []).map((ra: any) => ra.userId)

  const handleInitialValues = (bookingData: any) => {
    const {
      pricePerNight,
      checkIn,
      checkOut,
      earnings,
      propertyId,
      firstName,
      lastName,
    } = bookingData
    const data = {
      ...bookingData
    }
    if (!earnings && pricePerNight) {
      const nights = timeService.calculateNights(checkIn, checkOut)
      const earnings = Number((Number(pricePerNight) * nights).toFixed(2))
      data.earnings = earnings
    }

    if (checkIn) {
      data.checkIn = new Date(checkIn)
    }

    if (checkOut) {
      data.checkOut = new Date(checkOut)
    }

    if (propertyId && propertyData && propertyData[propertyId]) {
      const propertyInfo = propertyData[propertyId]
      // set cleaning price
      if (!data.cleaning) {
        const cleaningPrice = propertyInfo && propertyInfo.pricing && propertyInfo.pricing.cleaning ? Number(propertyInfo.pricing.cleaning) : 0
        data.cleaning = cleaningPrice
      }
      // set booking guest info
      if (!data.requiresBookingGuestInfo) {
        const requiresBookingGuestInfo = propertyInfo.services.bookingGuestInfo
        data.requiresBookingGuestInfo = requiresBookingGuestInfo
      }
      // check if requires confirmation
      if (!data.requiresConfirmation) {
        const isFontaine = hasLinkedService(propertyInfo, 'fontaine', false)
        const isParentProperty = getChildProperties(propertyData, propertyInfo._id).length > 0
        const requiresConfirmation = isFontaine && !isParentProperty
        data.requiresConfirmation = requiresConfirmation
      }
    }

    if (firstName || lastName) {
      data.contactInfo = {
        firstName: '',
        lastName: '',
        email: '',
        phone: ''
      }
    }

    if (firstName) {
      data.contactInfo.firstName = firstName
    }
    if (lastName) {
      data.contactInfo.lastName = lastName
    }

    if (ownerBooking && !bookingData._id) {
      data.source = 'owner'
      data.bookingStatus = 'new'
    }

    delete data.firstName
    delete data.lastName

    delete data.quickBooking

    if (!data.source) {
      data.source = 'direct'
    }

    return data
  }

  const checkDateRange = async (propertyId: string, checkIn: Date, checkOut: Date) => {
    if (dateValidationFn && propertyId && checkIn && checkOut) {
      return dateValidationFn(propertyId, checkIn, checkOut)
    }
    return true
  }

  return (
    <div className="booking-form">
      <Formik
        initialValues={{
          ...blankBooking(),
          ...(initialValues ? handleInitialValues(initialValues) : {})
        }}
        validationSchema={bookingSchema({ isOwner: ownerBooking })}
        // validate={values => { const errors: any = {}; return errors; }}
        onSubmit={async (values, { setSubmitting }) => {
          try {
            const isAvailable = await checkDateRange(values.propertyId, values.checkIn, values.checkOut)
            if (!isAvailable && ownerBooking) {
              return
            }
            const submissionResponse = await onSubmit(values)
            if (!submissionResponse.error) {
              return
            }
          } catch (err) {
            // custom error handler
            setSubmitting(false)
          }
          setSubmitting(false)
        }}
      >
        {({ values, errors, setFieldValue, isSubmitting }) => {
          const isFontaine = values.propertyId && propertyData[values.propertyId] && hasLinkedService(propertyData[values.propertyId], 'fontaine', false)

          const showRemoveCleaningButton = values.earnings && values.cleaning && removeCleaningButtonActive

          return (
            <>
              <Form>
                {!quickBooking && (
                  <h3>ℹ️ General Info</h3>
                )}

                {quickBooking && (
                  <div>

                    {/* Blocked Refresh, Blocked No Refresh, Repairs Refresh, Repairs No Refresh */}
                    {[
                      ...(!ownerBooking ? [{
                        buttonText: '🎯 Direct - New',
                        source: 'direct',
                        bookingStatus: 'new',
                        disabled: values.source === 'direct' && values.bookingStatus === 'new'
                      },
                      {
                        buttonText: '🚫 Blocked - No Refresh',
                        source: 'blocked',
                        bookingStatus: 'request',
                        disabled: values.source === 'blocked' && values.bookingStatus === 'request'
                      },
                      {
                        buttonText: '🚫 Blocked - Refresh',
                        source: 'blocked',
                        bookingStatus: 'new',
                        disabled: values.source === 'blocked' && values.bookingStatus === 'new'
                      },
                      {
                        buttonText: '🔨 Repairs - No Refresh',
                        source: 'repairs',
                        bookingStatus: 'request',
                        disabled: values.source === 'repairs' && values.bookingStatus === 'request'
                      },
                      {
                        buttonText: '🔨 Repairs - Refresh',
                        source: 'repairs',
                        bookingStatus: 'new',
                        disabled: values.source === 'repairs' && values.bookingStatus === 'new'
                      }] : []),

                      ...(!values._id ? [
                        {
                          buttonText: '👤 Owner Booking',
                          source: 'owner',
                          bookingStatus: 'new',
                          disabled: values.source === 'owner' && values.bookingStatus === 'new'
                        },
                        {
                          buttonText: '👤 Owner Guest',
                          source: 'owner-guest',
                          bookingStatus: 'new',
                          disabled: values.source === 'owner-guest' && values.bookingStatus === 'new'
                        }
                      ] : []),


                    ].map((btn, idx) => {
                      return (
                        <Button
                          key={`quick-booking-btn-${idx}`}
                          className="me-2 mb-2"
                          variant={btn.disabled ? 'primary' : 'outline-secondary'}
                          disabled={btn.disabled}
                          size="sm"
                          onClick={() => {
                            if (btn.source === 'blocked' || btn.source === 'repairs') {
                              // change first and last name to "Blocked" or "Repairs"
                              setFieldValue('contactInfo.firstName', btn.source.charAt(0).toUpperCase() + btn.source.slice(1))
                              setFieldValue('contactInfo.lastName', 'Booking')
                            } else if (values?.contactInfo?.lastName === 'Booking') {
                              setFieldValue('contactInfo.firstName', '')
                              setFieldValue('contactInfo.lastName', '')
                            } else if (btn.source === 'owner') {
                              // clear first and last name
                              setFieldValue('contactInfo.firstName', '')
                              setFieldValue('contactInfo.lastName', '')
                            }
                            setTimeout(() => {
                              setFieldValue('source', btn.source)
                              setFieldValue('bookingStatus', btn.bookingStatus)
                            }, 100)
                          }}
                        >{btn.buttonText}</Button>
                      )
                    })}
                  </div>
                )}

                <DateRangeInputField
                  fieldName={'dateRange'}
                  fieldLabel={'Date Range'}
                  startDate={values.checkIn}
                  placeholder={'mm/dd/yyyy - mm/dd/yyyy'}
                  endDate={values.checkOut}
                  minDate={ownerBooking ? new Date() : undefined}
                  onChange={(dates: [Date | null, Date | null]) => {
                    if (!quickBooking) {
                      setFieldValue('earnings', timeService.calculateBookingTotal(values.pricePerNight, dates[0], dates[1]))
                    }
                    const checkInVal = timeService.setDateTime(dates[0], { hour: 16 })
                    const checkOutVal = timeService.setDateTime(dates[1], { hour: 11 })
                    setFieldValue('checkIn', checkInVal)
                    setFieldValue('checkOut', checkOutVal)
                    if (checkInVal && checkOutVal) {
                      checkDateRange(values.propertyId, checkInVal, checkOutVal)
                    }
                  }} />

                {!quickBooking && (
                  <>
                    <DateInputField
                      fieldLabel={'Check In'}
                      placeholder={'mm/dd/yyyy'}
                      onChange={(date) => {
                        setFieldValue('earnings', timeService.calculateBookingTotal(values.pricePerNight, date, values.checkOut))
                        checkDateRange(values.propertyId, date, values.checkOut)
                      }}
                      maxDate={values.checkOut}
                      fieldName={'checkIn'} />

                    <DateInputField
                      fieldLabel={'Check Out'}
                      placeholder={'mm/dd/yyyy'}
                      onChange={(date) => {
                        console.log('check out date', date)
                        setFieldValue('earnings', timeService.calculateBookingTotal(values.pricePerNight, values.checkIn, date))
                        checkDateRange(values.propertyId, values.checkIn, date)
                      }}
                      minDate={values.checkIn}
                      fieldName={'checkOut'} />
                  </>
                )}


                <SelectInputField
                  fieldLabel={'Property Id'}
                  placeholder={'- Select Property -'}
                  required={true}
                  onChange={(e: any) => {
                    setFormPropertyId(e.target.value)
                    if (e.target.value && propertyData[e.target.value]) {
                      const propertyInfo = propertyData[e.target.value]

                      // set cleaning price
                      const cleaningPrice = propertyInfo && propertyInfo.pricing && propertyInfo.pricing.cleaning ? Number(propertyInfo.pricing.cleaning) : 0
                      setFieldValue('cleaning', cleaningPrice)

                      // set booking guest info
                      const requiresBookingGuestInfo = propertyInfo.services.bookingGuestInfo
                      setFieldValue('requiresBookingGuestInfo', requiresBookingGuestInfo)

                      // check if requires confirmation
                      const isFontaine = hasLinkedService(propertyInfo, 'fontaine', false)
                      const isParentProperty = getChildProperties(propertyData, propertyInfo._id).length > 0
                      const requiresConfirmation = isFontaine && !isParentProperty
                      setFieldValue('requiresConfirmation', requiresConfirmation)

                      checkDateRange(e.target.value, values.checkIn, values.checkOut)
                    }
                  }}
                  options={propertyOptionList}
                  fieldName={'propertyId'} />

                {dateValidationStatus && dateValidationStatus.status && values.checkIn && values.checkOut && values.propertyId && (
                  <Alert variant={dateValidationStatus.status}>
                    {dateValidationStatus.message}
                  </Alert>
                )}

                <hr />

                {!quickBooking && (
                  <h3>👤 Booking Contact Info</h3>
                )}

                {values.source === 'owner' && (
                  <div className="mb-2">
                    <Button
                      size="sm"
                      variant="outline-primary"
                      disabled={!values.propertyId}
                      onClick={() => setSelectOwnerModal(true)}
                    >
                      👤 Select Owner
                    </Button>
                    {!values.propertyId && (
                      <span className="ms-2 text-muted small">Select a property first</span>
                    )}
                  </div>
                )}
                <Row>
                  <Col xs={12} md={6}>
                    <TextInputField
                      fieldLabel={'First Name'}
                      placeholder={'First Name'}
                      disabled={values.source === 'owner' && ownerBooking}
                      fieldName={'contactInfo.firstName'} />

                  </Col>
                  <Col xs={12} md={6}>
                    <TextInputField
                      fieldLabel={'Last Name'}
                      placeholder={'Last Name'}
                      disabled={values.source === 'owner' && ownerBooking}
                      fieldName={'contactInfo.lastName'} />
                  </Col>
                </Row>

                {!quickBooking && (
                  <Row>
                    <Col xs={12} md={6}>
                      <TextInputField
                        fieldLabel={'Email'}
                        fieldType={'email'}
                        placeholder={'Email'}
                        fieldName={'contactInfo.email'} />
                    </Col>
                    <Col xs={12} md={6}>
                      <PhoneNumberInputField
                        fieldLabel="Phone"
                        placeholder="Phone"
                        fieldName={`contactInfo.phone`} />
                    </Col>
                  </Row>
                )}

                <hr />

                {!quickBooking && (
                  <>
                    <h3>👤 Booking Guest Info</h3>

                    <CheckBoxInputField
                      fieldLabel={'Requires Booking Guest Info'}
                      fieldName={'requiresBookingGuestInfo'} />

                    <TextInputField
                      fieldLabel={'Booking Guest Info Id'}
                      placeholder={'Booking Guest Info Id'}
                      fieldName={'bookingGuestInfoId'} />

                    <hr />

                    <h3>🫂 Guest Counts</h3>

                    <TextInputField
                      fieldLabel={'Total Guest Count'}
                      fieldType={'number'}
                      placeholder={'Guest Count'}
                      fieldName={'guestInfo.guestTotal'} />

                    <TextInputField
                      fieldLabel={'Adults'}
                      fieldType={'number'}
                      placeholder={'Adults'}
                      fieldName={'guestInfo.adults'} />

                    <TextInputField
                      fieldLabel={'Children'}
                      fieldType={'number'}
                      placeholder={'Children'}
                      fieldName={'guestInfo.children'} />

                    <TextInputField
                      fieldLabel={'Infants'}
                      fieldType={'number'}
                      placeholder={'Infants'}
                      fieldName={'guestInfo.infants'} />

                    <TextInputField
                      fieldLabel={'Pets'}
                      fieldType={'number'}
                      placeholder={'Pets'}
                      fieldName={'guestInfo.pets'} />

                    <hr />
                  </>
                )}


                {!quickBooking && (
                  <h3>📋 Booking Details</h3>
                )}


                <Row>
                  <Col>
                    <TextInputField
                      fieldLabel={'Earnings'}
                      fieldType={'number'}
                      disabled={!values.checkIn || !values.checkOut}
                      onChange={(e) => {
                        setFieldValue('pricePerNight', timeService.calculatePricePerNight(e.target.value, values.checkIn, values.checkOut))
                        setRemoveCleaningButtonActive(true)
                      }}
                      placeholder={'Earnings'}
                      fieldName={'earnings'} />
                  </Col>
                  {showRemoveCleaningButton && (
                    <Col xs="auto">
                      <Button
                        className="my-2"
                        variant={'secondary'}
                        onClick={() => {
                          setRemoveCleaningButtonActive(false)
                          const newTotal = values.earnings - values.cleaning > 0 ? values.earnings - values.cleaning : 0
                          setFieldValue('earnings', newTotal)
                          setFieldValue('pricePerNight', timeService.calculatePricePerNight(newTotal, values.checkIn, values.checkOut))
                        }}
                      >
                        Remove Cleaning (${values.cleaning})
                      </Button>
                    </Col>
                  )}
                </Row>

                <hr />

                {quickBooking && !ownerBooking && (
                  <Collapsible
                    openText="Show Extra Options"
                    closeText="Hide Extra Options"
                  >
                    <>
                      <TextInputField
                        fieldLabel={'Price Per Night'}
                        fieldType={'number'}
                        disabled
                        placeholder={'Price Per Night'}
                        fieldName={'pricePerNight'} />

                      <TextInputField
                        fieldLabel={'Cleaning'}
                        fieldType={'number'}
                        placeholder={'Cleaning'}
                        fieldName={'cleaning'} />

                      {values.source === '' || bookingSourceFieldValuesRef[values.source] ? (
                        <SelectInputField
                          fieldLabel={'Booking Source'}
                          placeholder={'- Select Booking Source -'}
                          options={bookingSourceFieldValues}
                          fieldName={'source'} />
                      ) : (
                        <TextInputField
                          fieldLabel={'Booking Source'}
                          placeholder={'Booking Source'}
                          fieldName={'source'} />
                      )}

                      <TextInputField
                        fieldLabel={'Booking Source Confirmation Code'}
                        placeholder={'Booking Source Confirmation Code'}
                        fieldName={'sourceConfirmation'} />

                      <hr />

                      <h5>Confirmation</h5>
                      {canAccess('booking', 'form.requiresConfirmation') && isFontaine && (
                        <>
                          <CheckBoxInputField
                            fieldLabel={'Requires Confirmation'}
                            fieldName={'requiresConfirmation'} />

                          <TextInputField
                            fieldLabel={'Confirmation Code'}
                            placeholder={'Confirmation Code'}
                            fieldName={'confirmationCode'} />
                        </>
                      )}
                      <SelectInputField
                        fieldLabel={'Booking Status'}
                        placeholder={'- Select Booking Status -'}
                        required={true}
                        options={bookingStatusFieldValues}
                        fieldName={'bookingStatus'} />

                      <br />
                    </>
                  </Collapsible>
                )}

                {!quickBooking && (
                  <>
                    <TextInputField
                      fieldLabel={'Price Per Night'}
                      fieldType={'number'}
                      disabled
                      placeholder={'Price Per Night'}
                      fieldName={'pricePerNight'} />

                    <TextInputField
                      fieldLabel={'Cleaning'}
                      fieldType={'number'}
                      placeholder={'Cleaning'}
                      fieldName={'cleaning'} />

                    {values.source === '' || bookingSourceFieldValuesRef[values.source] ? (
                      <SelectInputField
                        fieldLabel={'Booking Source'}
                        placeholder={'- Select Booking Source -'}
                        options={bookingSourceFieldValues}
                        fieldName={'source'} />
                    ) : (
                      <TextInputField
                        fieldLabel={'Booking Source'}
                        placeholder={'Booking Source'}
                        fieldName={'source'} />
                    )}

                    <TextInputField
                      fieldLabel={'Booking Source Confirmation Code'}
                      placeholder={'Booking Source Confirmation Code'}
                      fieldName={'sourceConfirmation'} />

                    <hr />
                    <h3>✔️ Confirmation</h3>

                    {canAccess('booking', 'form.requiresConfirmation') && isFontaine && (
                      <>
                        <CheckBoxInputField
                          fieldLabel={'Requires Confirmation'}
                          fieldName={'requiresConfirmation'} />

                        <TextInputField
                          fieldLabel={'Confirmation Code'}
                          placeholder={'Confirmation Code'}
                          fieldName={'confirmationCode'} />
                      </>
                    )}

                    <SelectInputField
                      fieldLabel={'Booking Status'}
                      placeholder={'- Select Booking Status -'}
                      required={true}
                      options={bookingStatusFieldValues}
                      fieldName={'bookingStatus'} />

                  </>
                )}


                {!quickBooking && (
                  <>
                    <hr />
                    <h3>⛔ Cancellation Policy</h3>

                    <TextInputField
                      fieldLabel={'Cancellation Amount'}
                      fieldType={'number'}
                      placeholder={'Cancellation Amount'}
                      fieldName={'cancellation.amount'} />

                    <SelectInputField
                      fieldLabel={'Cancellation Type'}
                      placeholder={'- Select Type -'}
                      required={true}
                      options={cancellationAmountTypeFieldValues}
                      fieldName={'cancellation.amountType'} />

                    <hr />

                    <h3>💸 Fees</h3>

                    <ArrayFieldInput
                      fieldName={'fees'}
                      values={values.fees || []}
                      setFieldValue={setFieldValue}
                      blankEntryFn={() => ({ feeType: '', feeSource: '', feeAmount: '' })}
                      fields={bookingFeesFieldValues} />

                    <hr />

                    <h3>ℹ️ Booking Extra Info</h3>

                    <TextInputField
                      fieldLabel={'Notes'}
                      fieldType={'textarea'}
                      placeholder={'Notes'}
                      fieldName={'notes'} />

                    <DateInputField
                      fieldLabel={'Date Booking was Made'}
                      placeholder={'mm/dd/yyyy'}
                      fieldName={'dateBooked'} />

                    <DateInputField
                      fieldLabel={'Date Booking was Inquired'}
                      placeholder={'mm/dd/yyyy'}
                      fieldName={'dateInquired'} />

                    <DateInputField
                      fieldLabel={'Date Booking was Updated'}
                      placeholder={'mm/dd/yyyy'}
                      fieldName={'dateUpdated'} />

                    <TextInputField
                      fieldLabel={'Scrape Id'}
                      placeholder={'Scrape Id'}
                      fieldName={'scrapeId'} />

                    <TextInputField
                      fieldLabel={'Turnover Id'}
                      placeholder={'Turnover Id'}
                      fieldName={'turnoverId'} />

                    <Row>
                      <Col xs={12} md={6}>
                        <TextInputField
                          fieldLabel={'Created By'}
                          placeholder={'Created By'}
                          fieldName={'createdBy'} />
                      </Col>
                      {values.source === 'vrbo' && (
                        <Col xs={12} md={6} className="d-flex align-items-center">
                          {values.createdBy === 'server_vrbo_olb' ? (
                            <Button
                              size="sm"
                              variant="outline-danger"
                              onClick={() => {
                                setFieldValue('createdBy', '')
                                setFieldValue('linkedServices', values.linkedServices.filter(
                                  (ls: any) => ls.linkedService !== 'vrbo-integration' && ls.linkedService !== 'vrbo-integration-child'
                                ))
                              }}
                            >
                              VRBO Unlink Olb
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              variant="outline-primary"
                              onClick={() => {
                                setFieldValue('createdBy', 'server_vrbo_olb')
                                setFieldValue('linkedServices', [
                                  ...values.linkedServices,
                                  {
                                    linkedService: 'vrbo-integration',
                                    linkedServiceId: '{}',
                                    linkedServicePrice: values.earnings || 0,
                                  }
                                ])
                              }}
                            >
                              VRBO Link Olb
                            </Button>
                          )}
                        </Col>
                      )}
                    </Row>

                    <Collapsible
                      title="🤖 Linked Services"
                      TitleTag="h3"
                    >
                      <div>
                        <label htmlFor="Linked Services">Linked Services</label><br />

                        <BookingLinkedServices
                          linkedServices={values.linkedServices}
                          setFieldValue={setFieldValue} />

                        <ArrayFieldInput
                          fieldName={'linkedServices'}
                          values={values.linkedServices}
                          setFieldValue={setFieldValue}
                          blankEntryFn={() => ({ linkedService: '', linkedServiceId: '' })}
                          fields={linkedServicesFieldValues} />
                      </div>
                    </Collapsible>

                  </>
                )}


                {Object.keys(errors).length > 0 && (
                  <Alert variant="danger">
                    {/* Errors: {JSON.stringify(errors)} */}
                    {Object.keys(errors).map((field, idx) => {
                      return (
                        <div key={`error-${idx}`}>
                          {field}: {JSON.stringify(errors[field])}
                        </div>
                      )
                    })}
                  </Alert>
                )}
                <hr />
                <Button
                  className="mb-3"
                  type="submit"
                  disabled={Object.keys(errors).length > 0 || isSubmitting || disabled || (ownerBooking && dateValidationStatus && dateValidationStatus.available === false)}
                >
                  Submit
                </Button>
              </Form>

              <SelectUserModal
                title="Select Property Owner"
                message="Select the owner for this property"
                active={selectOwnerModal}
                onClose={() => setSelectOwnerModal(false)}
                onSelect={(selectedUser: any) => {
                  const { firstName, lastName } = selectedUser
                  setFieldValue('contactInfo.firstName', firstName)
                  setTimeout(() => {
                    setFieldValue('contactInfo.lastName', lastName)
                  }, 100)
                  setSelectOwnerModal(false)
                }}
                filterFn={(user) => propertyOwnerIds.includes(user._id)}
              />

              <SelectUserModal
                active={selectUserModal}
                onClose={() => {
                  setSelectUserModal(false)
                }}
                onSelect={(selectedUser: any) => {
                  console.log(selectedUser)
                  const { _id, firstName, lastName } = selectedUser
                  setFieldValue('linkedServices', [
                    ...values.linkedServices,
                    {
                      linkedService: 'ownerBooking',
                      linkedServiceId: JSON.stringify({
                        userId: _id,
                        name: `${firstName} ${lastName}`,
                      })
                    }
                  ])
                  if (values.notes) {
                    setFieldValue('notes', `${values.notes}\nOwner Booking`)
                  } else {
                    setFieldValue('notes', `Owner Booking`)
                  }

                  setFieldValue('source', 'owner')

                  // if there is not first name or last name in the booking data, set them to the selected user
                  if (!values.contactInfo.firstName && !values.contactInfo.lastName) {
                    setFieldValue('contactInfo.firstName', firstName)
                    setFieldValue('contactInfo.lastName', lastName)
                  }
                  setSelectUserModal(false)
                }}
              />
            </>
          )
        }}
      </Formik>
    </div>
  )
}
